<template>
  <div class="video">
    <video
      ref="video"
      :controls="controls ? true : false"
      class="video__video lazyload"
      loading="lazy"
      :loop="loop ? true : false"
      :muted="muted ? true : false"
      playsinline="1"
      preload="auto"
      :poster="poster"
      @pause="onPause"
      @play="onPlay"
      @click="togglePlay"
    >
      <source
        v-if="src"
        type="video/mp4"
        :src="src"
      >

      <template v-else-if="srcset">
        <source
          v-if="getHlsSource(srcset)"
          :src="getHlsSource(srcset)"
          type="application/x-mpegURL"
        >
        <source
          v-if="getSdSource(srcset)"
          :src="getSdSource(srcset)"
          type="video/mp4"
        >
      </template>
    </video>

    <div class="video__controls">
      <button
        v-show="!isPlaying"
        class="icon-button"
        type="button"
        aria-label="Play Video"
        @click="handlePlay"
      >
        <icon-start />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Video element (video-element)
 * -----------------------------------------------------------------------------
 * Component to display video element.
 *
 */
import { mapState } from 'vuex'

import IconStart from '~icons/video/start.svg'

export default {
  name: 'VideoElement',

  components: {
    IconStart,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    poster: {
      type: String,
      default: '',
    },
    src: {
      type: String,
      default: '',
    },
    srcset: {
      type: Object,
      default: () => ({}),
    },
    controls: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    muted: {
      type: Boolean,
      default: false,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isPausing: false,
      isPlaying: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isDesktop: (state) => state.index.screen.desktop,
    }),
  },

  mounted() {
    if (this.autoplay) {
      this.handlePlay()
    }
  },

  methods: {

    /**
     * Toggle play/pause video.
     */
    togglePlay() {
      if (this.isPlaying) {
        this.handlePause()
      } else {
        this.handlePlay()
      }
    },

    /**
     * Pause all videos in the document.
     */
    pauseAllVideos() {
      const video = document.querySelectorAll('video')

      if (!video) {
        return
      }

      video.forEach((element) => {
        element.pause()
      })
    },

    /**
     * Handle play video.
     */
    handlePlay() {
      this.pauseAllVideos()
      this.$refs.video.play()
    },

    /**
     * On video play event.
     */
    onPlay() {
      this.isPausing = false
      this.isPlaying = true
    },

    /**
     * On video pause event.
     */
    onPause() {
      this.isPausing = true
      this.isPlaying = false
    },

        /**

    /**
     * Get M3U8 Video source
     * @param {Object} sources - List of video sources returned from GraphQL.
     * @returns {String}
     */
    getHlsSource(sources) {
      const hls = sources.find((source) => source.format === 'm3u8')
      return hls ? hls.url : false
    },

    /**
     * Get SD Video source
     * @param {Object} sources - List of video sources returned from GraphQL.
     * @returns {String}
     */
    getSdSource(sources) {
      const sd = sources.find((source) => source.url.includes('.SD'))
      return sd ? sd.url : false
    },

  },
}
</script>

<style lang="scss">
@import './video-element';
</style>
